const millisecondMap = {
  milliseconds: 1,
  seconds: 1_000,
  minutes: 60_000,
  hours: 3_600_000,
  days: 86_400_000,
  weeks: 604_800_000,
  months: 2_592_000_000,
  years: 31_536_000_000,
} as const;

export type DurationUnit = keyof typeof millisecondMap;

export type DurationOptions = Partial<Record<DurationUnit, number>>;

function resolveDuration(options: DurationOptions): number {
  return Object.entries(options).reduce(
    (acc, [key, value]) => acc + (value ?? 0) * millisecondMap[key as DurationUnit],
    0,
  );
}

export type DurationUnitConversion = {
  from: DurationUnit;
  to: DurationUnit;
};

export class Duration {
  private readonly milliseconds: number;

  constructor(milliseconds: number) {
    this.milliseconds = milliseconds;
  }

  static from(options: DurationOptions): Duration {
    return new Duration(resolveDuration(options));
  }

  static fromUnit(value: number, unit: DurationUnit): Duration {
    return Duration.from({ [unit]: value });
  }

  static convert(value: number, units: DurationUnitConversion): number {
    return Duration.from({ [units.from]: value }).toUnit(units.to);
  }

  toUnit(unit: DurationUnit): number {
    return this.milliseconds / millisecondMap[unit];
  }

  toMilliseconds(): number {
    return this.milliseconds;
  }

  toMillis(): number {
    return this.milliseconds;
  }

  toSeconds(): number {
    return this.toUnit('seconds');
  }

  toMinutes(): number {
    return this.toUnit('minutes');
  }

  toHours(): number {
    return this.toUnit('hours');
  }

  toDays(): number {
    return this.toUnit('days');
  }

  toWeeks(): number {
    return this.toUnit('weeks');
  }

  toMonths(): number {
    return this.toUnit('months');
  }

  toYears(): number {
    return this.toUnit('years');
  }
}
